import React, { useState } from 'react';
import { Trophy, Calendar, DollarSign, TrendingUp, Eye, Star, RefreshCw } from 'lucide-react';

const HorseCollection: React.FC = () => {
  const [selectedHorse, setSelectedHorse] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [favorites, setFavorites] = useState<number[]>([]);

  const horses = [
    {
      id: 1,
      name: 'Thunder Bay',
      age: 4,
      wins: 7,
      races: 12,
      earnings: 340000,
      status: 'racing',
      nextRace: 'Keeneland - Today',
      image: '🏇',
      stats: { speed: 95, stamina: 88, form: 92 }
    },
    {
      id: 2,
      name: 'Golden Storm',
      age: 3,
      wins: 4,
      races: 8,
      earnings: 125000,
      status: 'training',
      nextRace: 'Training - 2 weeks',
      image: '🐎',
      stats: { speed: 89, stamina: 85, form: 87 }
    },
    {
      id: 3,
      name: 'Silver Arrow',
      age: 5,
      wins: 9,
      races: 15,
      earnings: 485000,
      status: 'breeding',
      nextRace: 'Breeding Season',
      image: '🏇',
      stats: { speed: 92, stamina: 90, form: 94 }
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'racing': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'training': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'breeding': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setToastMessage('Horse data refreshed successfully!');
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      setToastMessage('Failed to refresh data');
      setTimeout(() => setToastMessage(''), 3000);
    }
    setIsRefreshing(false);
  };

  const handleViewDetails = (horseId: number) => {
    setSelectedHorse(selectedHorse === horseId ? null : horseId);
    setToastMessage(`Viewing details for ${horses.find(h => h.id === horseId)?.name}`);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleToggleFavorite = (horseId: number) => {
    setFavorites(prev => 
      prev.includes(horseId) 
        ? prev.filter(id => id !== horseId)
        : [...prev, horseId]
    );
    const horse = horses.find(h => h.id === horseId);
    setToastMessage(
      favorites.includes(horseId) 
        ? `Removed ${horse?.name} from favorites`
        : `Added ${horse?.name} to favorites`
    );
    setTimeout(() => setToastMessage(''), 3000);
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slideIn">
          {toastMessage}
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Champion Stable</h1>
            <p className="text-slate-400">Meet the thoroughbreds racing for our syndicate</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center space-x-2 bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>

        {/* Horses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {horses.map((horse) => (
            <div key={horse.id} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-200">
              {/* Horse Image & Name */}
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{horse.image}</div>
                <h3 className="text-2xl font-bold text-white mb-2">{horse.name}</h3>
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <button
                    onClick={() => handleToggleFavorite(horse.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      favorites.includes(horse.id) 
                        ? 'bg-yellow-500/20 text-yellow-400' 
                        : 'bg-slate-700 text-slate-400 hover:text-yellow-400'
                    }`}
                  >
                    <Star className={`w-4 h-4 ${favorites.includes(horse.id) ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleViewDetails(horse.id)}
                    className="p-2 rounded-lg bg-slate-700 text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(horse.status)}`}>
                  {horse.status.charAt(0).toUpperCase() + horse.status.slice(1)}
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Age</span>
                  <span className="text-white font-medium">{horse.age} years old</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Record</span>
                  <span className="text-white font-medium">{horse.wins}W / {horse.races} races</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Earnings</span>
                  <span className="text-green-400 font-medium">${(horse.earnings / 1000).toFixed(0)}K</span>
                </div>
              </div>

              {/* Performance Stats */}
              <div className="mb-6">
                <h4 className="text-white font-semibold mb-3">Performance Stats</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Speed</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="performance-fill-speed"
                          style={{ width: `${horse.stats.speed}%` }}
                        ></div>
                      </div>
                      <span className="text-white text-sm">{horse.stats.speed}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Stamina</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="performance-fill-stamina"
                          style={{ width: `${horse.stats.stamina}%` }}
                        ></div>
                      </div>
                      <span className="text-white text-sm">{horse.stats.stamina}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Form</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="performance-fill-form"
                          style={{ width: `${horse.stats.form}%` }}
                        ></div>
                      </div>
                      <span className="text-white text-sm">{horse.stats.form}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Race */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-slate-400 text-sm">Next Race</span>
                </div>
                <div className="text-white font-medium">{horse.nextRace}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Collection Stats */}
        <div className="mt-12 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Collection Statistics</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-400 mb-2">12</div>
              <div className="text-slate-400">Total Horses</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-2">$2.1M</div>
              <div className="text-slate-400">Total Earnings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400 mb-2">68%</div>
              <div className="text-slate-400">Win Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400 mb-2">3</div>
              <div className="text-slate-400">Breeding Pairs</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorseCollection;
